"use client";

import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import type { ChatStatus } from "ai";
import { type ReactElement, useRef } from "react";
import { useChatToolPartPlugins } from "@/lib/ai/chat-client-plugin";
import { ChatMessageRenderer } from "@/lib/ai/chat-message-renderer";
import type {
  ChatAddToolApprovalResponse,
  ChatAddToolResult,
  ChatLogContext,
  ChatRetryApprovalContinuation,
  ChatSendMessage,
} from "@/lib/ai/chat-message-renderer-contract";
import { PendingResponseRow } from "@/lib/ai/message-view/pending-response-row";
import { ApprovalSubmissionProvider } from "@/lib/ai/tools/ui/native-tool-approval";
import type { AgentUIMessage } from "@/lib/ai/types";

type Props = {
  messages: AgentUIMessage[];
  /** Enabled only by the app-wide admin authorization at the thread boundary. */
  showResponseModel?: boolean;
  creatorName?: string;
  creatorPicture?: string | null;
  addToolResult: ChatAddToolResult;
  addToolApprovalResponse: ChatAddToolApprovalResponse;
  sendMessage: ChatSendMessage;
  requestApprovalContinuation: ChatSendMessage;
  canRetryApprovalContinuation?: boolean;
  isRetryingApprovalContinuation?: boolean;
  retryApprovalContinuation?: ChatRetryApprovalContinuation;
  retryableApprovalToolCallId?: string;
  chatStatus: ChatStatus;
  isPersistingMessages: boolean;
  logContext?: ChatLogContext;
};

/**
 * thread 層向けの薄い message renderer adapter。
 *
 * @remarks
 * plugin 収集と renderer 初期化だけをここで行い、描画本体は共通 host 側へ寄せる。
 */
export function ChatMessages({
  messages,
  showResponseModel,
  creatorName,
  creatorPicture,
  addToolResult,
  addToolApprovalResponse,
  sendMessage,
  requestApprovalContinuation,
  canRetryApprovalContinuation,
  isRetryingApprovalContinuation,
  retryApprovalContinuation,
  retryableApprovalToolCallId,
  chatStatus,
  isPersistingMessages,
  logContext,
}: Props): ReactElement {
  const { i18n } = useLingui();
  const copyLabel = i18n._(msg`Copy to clipboard`);
  const toolPartPlugins = useChatToolPartPlugins(messages);
  const responseStartedAtMsRef = useRef<number | null>(null);
  const responseIsOngoing =
    chatStatus === "submitted" || chatStatus === "streaming";

  if (responseIsOngoing && responseStartedAtMsRef.current === null) {
    responseStartedAtMsRef.current = Date.now();
  } else if (!responseIsOngoing) {
    responseStartedAtMsRef.current = null;
  }

  const responseStartedAtMs = responseStartedAtMsRef.current;
  const showPendingResponse =
    chatStatus === "submitted" && messages.at(-1)?.role === "user";

  return (
    <ApprovalSubmissionProvider>
      <ChatMessageRenderer
        messages={messages}
        showResponseModel={showResponseModel}
        creatorName={creatorName}
        creatorPicture={creatorPicture}
        addToolResult={addToolResult}
        addToolApprovalResponse={addToolApprovalResponse}
        sendMessage={sendMessage}
        requestApprovalContinuation={requestApprovalContinuation}
        canRetryApprovalContinuation={canRetryApprovalContinuation}
        isRetryingApprovalContinuation={isRetryingApprovalContinuation}
        retryApprovalContinuation={retryApprovalContinuation}
        retryableApprovalToolCallId={retryableApprovalToolCallId}
        chatStatus={chatStatus}
        isPersistingMessages={isPersistingMessages}
        copyLabel={copyLabel}
        toolPartPlugins={toolPartPlugins}
        logContext={logContext}
        responseStartedAtMs={responseStartedAtMs ?? undefined}
      />
      {showPendingResponse && responseStartedAtMs !== null ? (
        <PendingResponseRow startedAtMs={responseStartedAtMs} />
      ) : null}
    </ApprovalSubmissionProvider>
  );
}
